import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Head, router } from '@inertiajs/react';
import { 
  Search, 
  Upload, 
  Trash2, 
  Copy, 
  Download, 
  Check, 
  X, 
  Grid3X3, 
  Grid2X2, 
  Grid,
  Maximize2,
  Loader2,
  AlertCircle,
  FileImage
} from 'lucide-react';
import DashboardLayout from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogDescription,
  DialogFooter 
} from '@/components/ui/dialog';
import { 
  AlertDialog, 
  AlertDialogAction, 
  AlertDialogCancel, 
  AlertDialogContent, 
  AlertDialogDescription, 
  AlertDialogFooter, 
  AlertDialogHeader, 
  AlertDialogTitle 
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';

// Types
interface UsedByPost {
  id: number;
  title: string;
  slug: string;
  type: 'cover_image' | 'content_image';
  visibility: 'visible' | 'auth_only';
}

interface PaginationInfo {
  page: number;
  per_page: number;
  total_count: number;
  total_pages: number;
}

interface Asset {
  id: number;
  blob_id: number;
  filename: string;
  url: string;
  signed_id: string;
  byte_size: number;
  content_type: string;
  created_at: string;
  in_use: boolean;
  used_by: UsedByPost[];
  metadata: Record<string, unknown>;
}

type GridSize = 'small' | 'medium' | 'large';

// Helper function to format file size
function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

// Helper function to format date
function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

const AssetsIndex: React.FC = () => {
  // State
  const [assets, setAssets] = useState<Asset[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [gridSize, setGridSize] = useState<GridSize>('medium');
  const [selectedAssets, setSelectedAssets] = useState<Set<number>>(new Set());
  
  // Modal states
  const [previewAsset, setPreviewAsset] = useState<Asset | null>(null);
  const [deleteAsset, setDeleteAsset] = useState<Asset | null>(null);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  
  // Upload state
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Pagination state
  const [pagination, setPagination] = useState<PaginationInfo>({
    page: 1,
    per_page: 50,
    total_count: 0,
    total_pages: 0
  });

  // Fetch assets from API
  const fetchAssets = useCallback(async (page: number = 1, perPage: number = 50) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`/api/images?page=${page}&per_page=${perPage}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch assets');
      }
      
      const data = await response.json();
      setAssets(data.images || []);
      if (data.pagination) {
        setPagination(data.pagination);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load assets');
      console.error('Error fetching assets:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchAssets(pagination.page, pagination.per_page);
  }, [fetchAssets, pagination.page, pagination.per_page]);

  // Filter assets by search query
  const filteredAssets = assets.filter(asset => 
    asset.filename.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Handle file selection for upload
  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    await uploadFiles(Array.from(files));
    
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Upload files
  const uploadFiles = async (files: File[]) => {
    setUploading(true);
    setUploadProgress(0);

    try {
      const totalFiles = files.length;
      let uploadedCount = 0;

      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);

        const response = await fetch('/api/images', {
          method: 'POST',
          body: formData,
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Upload failed');
        }

        uploadedCount++;
        setUploadProgress(Math.round((uploadedCount / totalFiles) * 100));
      }

      toast.success(`${totalFiles} file(s) uploaded successfully`);
      await fetchAssets();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Upload failed');
      console.error('Upload error:', err);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  // Handle drag and drop
  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files).filter(file => 
      file.type.startsWith('image/')
    );
    
    if (files.length > 0) {
      await uploadFiles(files);
    }
  }, []);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  // Copy URL to clipboard
  const copyToClipboard = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('URL copied to clipboard');
    } catch (err) {
      toast.error('Failed to copy URL');
      console.error('Copy error:', err);
    }
  };

  // Download asset
  const downloadAsset = async (asset: Asset) => {
    try {
      const response = await fetch(asset.url);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = asset.filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Download started');
    } catch (err) {
      toast.error('Failed to download');
      console.error('Download error:', err);
    }
  };

  // Delete single asset
  const deleteAssetById = async (assetId: number) => {
    try {
      const response = await fetch(`/api/images/${assetId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Delete failed');
      }

      toast.success('Asset deleted successfully');
      setAssets(prev => prev.filter(a => a.id !== assetId));
      setSelectedAssets(prev => {
        const newSet = new Set(prev);
        newSet.delete(assetId);
        return newSet;
      });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Delete failed');
      console.error('Delete error:', err);
    }
  };

  // Bulk delete
  const bulkDelete = async () => {
    const ids = Array.from(selectedAssets);
    
    try {
      const response = await fetch('/api/images/bulk_destroy', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ids }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Bulk delete failed');
      }

      const data = await response.json();
      toast.success(`Deleted ${data.deleted_count} asset(s)`);
      await fetchAssets();
      setSelectedAssets(new Set());
      setBulkDeleteOpen(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Bulk delete failed');
      console.error('Bulk delete error:', err);
    }
  };

  // Toggle selection
  const toggleSelection = (id: number) => {
    setSelectedAssets(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  // Toggle select all
  const toggleSelectAll = () => {
    if (selectedAssets.size === filteredAssets.length) {
      setSelectedAssets(new Set());
    } else {
      setSelectedAssets(new Set(filteredAssets.map(a => a.id)));
    }
  };

  // Handle page change
  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= pagination.total_pages) {
      setPagination(prev => ({ ...prev, page: newPage }));
    }
  };

  // Handle per page change
  const handlePerPageChange = (newPerPage: number) => {
    setPagination(prev => ({ ...prev, per_page: newPerPage, page: 1 }));
  };

  // Grid size classes
  const gridSizeClasses = {
    small: 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8',
    medium: 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6',
    large: 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4',
  };

  // Image size based on grid
  const imageHeightClasses = {
    small: 'h-24',
    medium: 'h-40',
    large: 'h-64',
  };

  return (
    <DashboardLayout>
      <Head title="Assets" />

      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">Assets</h1>
            <p className="text-sm text-muted-foreground">
              Manage images and uploaded assets
            </p>
          </div>
          
          {/* Upload Button */}
          <div className="flex items-center gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={handleFileSelect}
            />
            <Button 
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              {uploading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="mr-2 h-4 w-4" />
                  Upload
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Upload Progress */}
        {uploading && (
          <div className="bg-muted rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-sm">Uploading... {uploadProgress}%</span>
            </div>
            <div className="h-2 bg-background rounded-full overflow-hidden">
              <div 
                className="h-full bg-primary transition-all duration-300"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
          </div>
        )}

        {/* Drop Zone (visible when dragging) */}
        <div 
          className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-8 text-center hover:border-muted-foreground/50 transition-colors"
          onDrop={handleDrop}
          onDragOver={handleDragOver}
        >
          <FileImage className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
          <p className="text-sm text-muted-foreground">
            Drag and drop images here, or use the Upload button above
          </p>
        </div>

        {/* Toolbar */}
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          {/* Search */}
          <div className="relative w-full sm:w-auto">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search assets..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 w-full sm:w-64"
            />
          </div>

          <div className="flex items-center gap-2 w-full sm:w-auto">
            {/* Bulk Actions */}
            {selectedAssets.size > 0 && (
              <div className="flex items-center gap-2 mr-4">
                <span className="text-sm text-muted-foreground">
                  {selectedAssets.size} selected
                </span>
                <Button 
                  variant="destructive" 
                  size="sm"
                  onClick={() => setBulkDeleteOpen(true)}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete
                </Button>
              </div>
            )}

            {/* Grid Size Toggle */}
            <div className="flex items-center gap-1 bg-muted rounded-md p-1">
              <Button
                variant={gridSize === 'small' ? 'secondary' : 'ghost'}
                size="sm"
                className="h-8 w-8 p-0"
                onClick={() => setGridSize('small')}
              >
                <Grid3X3 className="h-4 w-4" />
              </Button>
              <Button
                variant={gridSize === 'medium' ? 'secondary' : 'ghost'}
                size="sm"
                className="h-8 w-8 p-0"
                onClick={() => setGridSize('medium')}
              >
                <Grid2X2 className="h-4 w-4" />
              </Button>
              <Button
                variant={gridSize === 'large' ? 'secondary' : 'ghost'}
                size="sm"
                className="h-8 w-8 p-0"
                onClick={() => setGridSize('large')}
              >
                <Grid className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Pagination */}
        {pagination.total_pages > 1 && (
          <div className="flex items-center justify-between py-2">
            <div className="text-sm text-muted-foreground">
              Showing {((pagination.page - 1) * pagination.per_page) + 1} to {Math.min(pagination.page * pagination.per_page, pagination.total_count)} of {pagination.total_count} assets
            </div>
            <div className="flex items-center gap-2">
              <select
                value={pagination.per_page}
                onChange={(e) => handlePerPageChange(Number(e.target.value))}
                className="h-8 rounded-md border border-input bg-background px-2 text-sm"
              >
                <option value={25}>25 per page</option>
                <option value={50}>50 per page</option>
                <option value={100}>100 per page</option>
              </select>
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page <= 1}
                >
                  Previous
                </Button>
                <span className="text-sm text-muted-foreground px-2">
                  Page {pagination.page} of {pagination.total_pages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page >= pagination.total_pages}
                >
                  Next
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-destructive/10 border border-destructive rounded-lg p-4 flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-destructive" />
            <div>
              <p className="font-medium text-destructive">Error loading assets</p>
              <p className="text-sm text-muted-foreground">{error}</p>
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              className="ml-auto"
              onClick={fetchAssets}
            >
              Retry
            </Button>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && filteredAssets.length === 0 && (
          <div className="text-center py-12">
            <FileImage className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">No assets found</h3>
            <p className="text-sm text-muted-foreground mb-4">
              {searchQuery ? 'Try a different search term' : 'Upload your first image to get started'}
            </p>
          </div>
        )}

        {/* Assets Grid */}
        {!loading && !error && filteredAssets.length > 0 && (
          <>
            {/* Select All (when there are selected items) */}
            {selectedAssets.size > 0 && (
              <div className="flex items-center gap-2 mb-4">
                <input
                  type="checkbox"
                  checked={selectedAssets.size === filteredAssets.length}
                  onChange={toggleSelectAll}
                  className="h-4 w-4 rounded border-input"
                />
                <span className="text-sm text-muted-foreground">
                  Select all ({filteredAssets.length})
                </span>
              </div>
            )}

            <div className={`grid gap-4 ${gridSizeClasses[gridSize]}`}>
              {filteredAssets.map((asset) => (
                <div
                  key={asset.id}
                  className="group relative border rounded-lg overflow-hidden bg-card hover:border-primary/50 transition-colors"
                >
                  {/* Selection Checkbox */}
                  <div className="absolute top-2 left-2 z-10">
                    <input
                      type="checkbox"
                      checked={selectedAssets.has(asset.id)}
                      onChange={() => toggleSelection(asset.id)}
                      className="h-4 w-4 rounded border-input bg-background"
                    />
                  </div>

                  {/* In Use Badge - show individual post badges with visibility-based colors */}
                  {asset.in_use && asset.used_by && asset.used_by.length > 0 && (
                    <div className="absolute top-2 right-2 z-10 flex flex-wrap gap-1 justify-end max-w-[70%]">
                      {asset.used_by.map((post) => (
                        <a
                          key={`${post.id}-${post.type}`}
                          href={`/blog/${post.slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`px-2 py-0.5 text-[10px] font-medium rounded text-white transition-opacity hover:opacity-80 whitespace-nowrap ${
                            post.visibility === 'visible' 
                              ? 'bg-orange-500 hover:bg-orange-600'  // Orange for visible
                              : 'bg-purple-600 hover:bg-purple-700'  // Purple for auth_only
                          }`}
                          title={`${post.type === 'cover_image' ? 'Cover' : 'Content'} - ${post.visibility === 'visible' ? 'Public' : 'Auth Only'}`}
                        >
                          {post.slug}
                        </a>
                      ))}
                    </div>
                  )}

                  {/* Image */}
                  <div 
                    className={`cursor-pointer ${imageHeightClasses[gridSize]} w-full bg-muted`}
                    onClick={() => setPreviewAsset(asset)}
                  >
                    <img
                      src={asset.url}
                      alt={asset.filename}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>

                  {/* Info */}
                  <div className="p-3 space-y-2">
                    <p className="font-medium text-sm truncate" title={asset.filename}>
                      {asset.filename}
                    </p>
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>{formatFileSize(asset.byte_size)}</span>
                      <span>{formatDate(asset.created_at)}</span>
                    </div>
                  </div>

                  {/* Actions (shown on hover) */}
                  <div className="absolute bottom-[52px] left-0 right-0 p-2 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="flex items-center justify-center gap-2">
                      <Button
                        variant="secondary"
                        size="sm"
                        className="h-8 w-8 p-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          setPreviewAsset(asset);
                        }}
                        title="Preview"
                      >
                        <Maximize2 className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        className="h-8 w-8 p-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          copyToClipboard(asset.url);
                        }}
                        title="Copy URL"
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        className="h-8 w-8 p-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          downloadAsset(asset);
                        }}
                        title="Download"
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        className="h-8 w-8 p-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteAsset(asset);
                        }}
                        title="Delete"
                        disabled={asset.in_use}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Preview Modal */}
      <Dialog open={!!previewAsset} onOpenChange={() => setPreviewAsset(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>{previewAsset?.filename}</DialogTitle>
            <DialogDescription>
              Image details and actions
            </DialogDescription>
          </DialogHeader>
          
          {previewAsset && (
            <div className="space-y-4">
              {/* Image Preview */}
              <div className="bg-muted rounded-lg overflow-hidden">
                <img
                  src={previewAsset.url}
                  alt={previewAsset.filename}
                  className="w-full h-auto max-h-[50vh] object-contain"
                />
              </div>

              {/* Details */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Filename</p>
                  <p className="font-medium">{previewAsset.filename}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">File Size</p>
                  <p className="font-medium">{formatFileSize(previewAsset.byte_size)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Content Type</p>
                  <p className="font-medium">{previewAsset.content_type}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Uploaded</p>
                  <p className="font-medium">{formatDate(previewAsset.created_at)}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-muted-foreground">URL</p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 bg-muted px-2 py-1 rounded text-xs break-all">
                      {previewAsset.url}
                    </code>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => copyToClipboard(previewAsset.url)}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                
                {/* Used By */}
                {previewAsset.used_by && previewAsset.used_by.length > 0 && (
                  <div className="col-span-2">
                    <p className="text-muted-foreground mb-2">Used in:</p>
                    <div className="flex flex-wrap gap-2">
                      {previewAsset.used_by.map((post) => (
                        <a
                          key={`${post.id}-${post.type}`}
                          href={`/blog/${post.slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm text-white transition-opacity hover:opacity-80 ${
                            post.visibility === 'visible'
                              ? 'bg-orange-500 hover:bg-orange-600'
                              : 'bg-purple-600 hover:bg-purple-700'
                          }`}
                        >
                          <span className="text-[10px] uppercase tracking-wider opacity-75">
                            {post.type === 'cover_image' ? 'Cover' : 'Content'}
                          </span>
                          <span className="font-medium">{post.slug}</span>
                          <span className="text-[10px] opacity-75">
                            ({post.visibility === 'visible' ? 'Public' : 'Auth Only'})
                          </span>
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewAsset(null)}>
              Close
            </Button>
            {previewAsset && (
              <>
                <Button 
                  variant="outline"
                  onClick={() => downloadAsset(previewAsset)}
                >
                  <Download className="mr-2 h-4 w-4" />
                  Download
                </Button>
                <Button 
                  variant="destructive"
                  onClick={() => {
                    setPreviewAsset(null);
                    setDeleteAsset(previewAsset);
                  }}
                  disabled={previewAsset.in_use}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteAsset} onOpenChange={() => setDeleteAsset(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Asset</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteAsset?.filename}"? 
              {deleteAsset?.in_use && (
                <span className="text-destructive block mt-2">
                  Warning: This image is currently in use and may break content that references it.
                </span>
              )}
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteAsset && deleteAssetById(deleteAsset.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Delete Confirmation Dialog */}
      <AlertDialog open={bulkDeleteOpen} onOpenChange={setBulkDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selectedAssets.size} Assets</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {selectedAssets.size} assets? 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={bulkDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete {selectedAssets.size} Assets
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
};

export default AssetsIndex;
